import { GuestInvitationPanel } from '../components/project/GuestInvitationPanel.js';
import { ProjectMeetings } from '../components/project/ProjectMeetings.js';
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { ConversationRole, ROLES, defaultRoleFor, type Conversation, type DocumentKind, type DocumentPrompt, type MemberRole, type Meeting, type MeetingListResponse, type NavGroup, type Project, type ProjectMember, type ReasoningEffort } from '@jabberr/core';
import { DOCUMENT_KINDS } from '@jabberr/core';
import { api, ApiError, errorMessage } from '../lib/api.js';
import { conversationTitle, meetingTitle, duration, longDate, shortDate } from '../lib/format.js';
import { TALK_TITLE, firstNames, isTalk, liveVersion, pickableRoles, roleLabel } from '../lib/plain.js';
import { activeMemory, contextBullets, useKnows } from '../state/knows.js';
import { useDrawer } from '../state/drawer.js';
import { useSession } from '../state/session.js';
import { NAV_GROUPS, NAV_GROUP_LABELS } from '../lib/nav.js';
import { ChatIcon, MicIcon, PeopleIcon, TranscriptIcon } from '../components/icons.js';
import { EmptyState } from '../components/EmptyState.js';
import { ConfirmDialog, ErrorBox, Spinner } from '../components/ui.js';
import { TeamMeetingPicker } from '../components/TeamMeetingPicker.js';

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const a = words[0]?.[0];
  const b = words.length > 1 ? words[words.length - 1]?.[0] : undefined;
  return ((a ?? '?') + (b ?? '')).toUpperCase();
}

const MAX_MEMBERS_SHOWN = 6;
const RECENT_SHOWN = 8;

type ListedMeeting = MeetingListResponse['meetings'][number];

/** One row of "Recent": conversations, talks and meetings in one list, newest first, live meetings on top. */
export type RecentItem =
  | { type: 'conversation'; at: string; conversation: Conversation }
  | { type: 'meeting'; at: string; meeting: ListedMeeting };

/** Pure helper: the newest live meeting, or null when none is live. */
export function newestLiveMeeting(meetings: MeetingListResponse['meetings']): Meeting | null {
  const live = meetings.filter((m) => m.status === 'live').sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  return live[0] ?? null;
}

/** Pure helper: conversations and meetings as one list, live meetings first, then newest first. */
export function recentItems(conversations: Conversation[], meetings: ListedMeeting[]): RecentItem[] {
  const items: RecentItem[] = [
    ...conversations.map((c): RecentItem => ({ type: 'conversation', at: c.updatedAt, conversation: c })),
    ...meetings.map((m): RecentItem => ({ type: 'meeting', at: m.endedAt ?? m.startedAt, meeting: m })),
  ];
  const live = (i: RecentItem): number => (i.type === 'meeting' && i.meeting.status === 'live' ? 1 : 0);
  return items.sort((a, b) => live(b) - live(a) || b.at.localeCompare(a.at));
}

/** Pure helper: the plain line under a recent row ("Meeting · 32 min", "Talk · 8 min", "Conversation · Strategy · 4 messages"). */
export function recentMeta(item: RecentItem): string {
  if (item.type === 'meeting') {
    const m = item.meeting;
    const what = isTalk(m) ? 'Talk' : 'Meeting';
    if (m.status === 'live') return `${what} · live now`;
    const length = duration(m.startedAt, m.endedAt);
    return [what, length, m.recordingId ? 'transcript ready' : ''].filter(Boolean).join(' · ');
  }
  const c = item.conversation;
  const messages = `${c.lastTurnSeq} ${c.lastTurnSeq === 1 ? 'message' : 'messages'}`;
  if (c.kind === 'review') return `Transcript review · ${messages}`;
  return `Conversation · ${roleLabel(c.role)} · ${messages}`;
}

export function ProjectPage() {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const { user, refreshProjects } = useSession();
  const drawer = useDrawer();
  const [project, setProject] = useState<Project | null>(null);
  const [role, setRole] = useState<MemberRole | undefined>(undefined);
  const [canLinkAnchor, setCanLinkAnchor] = useState(false);
  const [canInviteGuests, setCanInviteGuests] = useState(false);
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [effort, setEffort] = useState<ReasoningEffort>('low');
  const [salesEnabled, setSalesEnabled] = useState(false);
  const [convRole, setConvRole] = useState<ConversationRole | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [meetings, setMeetings] = useState<MeetingListResponse['meetings'] | null>(null);
  const [endingMeeting, setEndingMeeting] = useState<string | null>(null);
  const [confirmEndMeeting, setConfirmEndMeeting] = useState<Meeting | null>(null);
  const [writing, setWriting] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const guest = role === 'guest';
  // Meetings (and so talks) are team work: the api refuses them for guests and in a personal space.
  const canMeet = project !== null && role !== undefined && !guest && project.kind !== 'personal';
  // Wait for the role, so a guest never asks for team knowledge.
  const knows = useKnows(role === undefined ? '' : projectId, { team: !guest });

  useEffect(() => {
    let current = true;
    void api.authConfig().then((config) => {
      if (current) setSalesEnabled(config.meetingPersonasEnabled === true);
    }).catch(() => { if (current) setSalesEnabled(false); });
    return () => { current = false; };
  }, []);

  const liveMeeting = meetings ? newestLiveMeeting(meetings) : null;

  const load = useCallback(async () => {
    try {
      const [p, c, m] = await Promise.all([
        api.projects.get(projectId),
        api.conversations.list(projectId),
        api.projects.members(projectId).catch(() => ({ members: [] })),
      ]);
      setProject(p.project);
      setRole(p.role);
      setCanLinkAnchor(p.canLinkAnchor ?? false);
      setCanInviteGuests(p.canInviteGuests ?? false);
      setConversations(c.conversations);
      setMembers(m.members);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotFound(true);
      else setError(errorMessage(err));
    }
  }, [projectId]);

  useEffect(() => {
    setConfirmEndMeeting(null);
    setProject(null);
    setCanLinkAnchor(false);
    setConvRole(null);
    setConversations(null);
    setMeetings(null);
    setNotFound(false);
    setError(null);
    void load();
    // Meetings are internal work: a guest gets 404 here, which leaves the list empty.
    api.meetings
      .list(projectId)
      .then((r) => setMeetings(r.meetings))
      .catch(() => setMeetings([]));
  }, [load, projectId]);

  // "Go to the project page" from a saved document (not linked to the Workbench yet) lands on the link field.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== '#workbench-project' || !project) return;
    const field = document.getElementById('workbench-project');
    field?.scrollIntoView({ block: 'center' });
    field?.focus();
  }, [hash, project]);

  useEffect(() => {
    drawer.setInfo({ projectId, title: project?.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, project?.name]);

  // A live meeting can be ended by someone else at any time; while one shows as live, check again so
  // the page does not keep saying Live after it ended.
  const anyLive = meetings?.some((m) => m.status === 'live') ?? false;
  useEffect(() => {
    if (!anyLive) return;
    const timer = window.setInterval(() => {
      api.meetings
        .list(projectId)
        .then((r) => setMeetings(r.meetings))
        .catch(() => undefined);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [anyLive, projectId]);

  const create = async (): Promise<void> => {
    setBusy(true);
    try {
      const body = { reasoningEffort: effort, ...(convRole ? { role: convRole } : {}) };
      const created = await api.conversations.create(projectId, body);
      navigate(`/p/${projectId}/c/${created.conversation.id}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /** A meeting: keen by default. A talk: a meeting for you and Jabberr, where he answers everything said. */
  const createMeeting = async (talk = false): Promise<void> => {
    setBusy(true);
    try {
      const { meeting } = await api.meetings.create(projectId, talk ? { title: TALK_TITLE, listenMode: 'always' } : { listenMode: 'keen' });
      navigate(`/meet/${meeting.id}`, { state: { inviteJabberr: talk } });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const endMeeting = async (m: Meeting): Promise<void> => {
    setEndingMeeting(m.id);
    try {
      const { meeting } = await api.meetings.end(m.id);
      setMeetings((prev) => (prev ?? []).map((x) => (x.id === m.id ? { ...x, status: meeting.status, recordingId: meeting.recordingId, endedAt: new Date().toISOString() } : x)));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setEndingMeeting(null);
    }
  };

  const archive = async (c: Conversation, status: 'active' | 'archived'): Promise<void> => {
    try {
      const { conversation } = await api.conversations.update(c.id, { status });
      setConversations((prev) => (prev ?? []).map((x) => (x.id === c.id ? conversation : x)));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const toggleProjectStatus = async (): Promise<void> => {
    if (!project) return;
    try {
      const { project: p } = await api.projects.update(projectId, { status: project.status === 'active' ? 'archived' : 'active' });
      setProject(p);
      await refreshProjects();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const [confirmShare, setConfirmShare] = useState(false);

  const share = async (): Promise<void> => {
    setConfirmShare(false);
    try {
      const { project: p } = await api.projects.update(projectId, { kind: 'team' });
      setProject(p);
      await refreshProjects();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const addEveryone = async (): Promise<void> => {
    try {
      await api.projects.addEveryone(projectId);
      setMembers((await api.projects.members(projectId)).members);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (notFound) {
    return (
      <div className="page">
        <h1>Project not found</h1>
        <p className="muted">You may not be a member of this project.</p>
        <Link to="/">Go home</Link>
      </div>
    );
  }

  const all = conversations ?? [];
  const active = all.filter((c) => c.status === 'active');
  const archived = all.filter((c) => c.status === 'archived');
  const isOwner = role === 'owner' || project?.createdBy === user?.id;
  const effectiveRole = convRole ?? (project ? defaultRoleFor(project.kind) : 'product_owner');
  const roleChoices = pickableRoles(salesEnabled);

  const eyebrow = !project
    ? ''
    : project.kind === 'personal'
      ? 'Just me'
      : project.kind === 'private' && project.isIdea
        ? `New project idea · private${members.length > 1 ? ` · ${firstNames(members.map((m) => m.user?.name ?? m.user?.email))}` : ''}`
        : project.kind === 'private'
        ? 'Just me · not shared yet'
        : guest
          ? 'Shared with you'
          : `With the team${members.length > 0 ? ` · ${firstNames(members.map((m) => m.user?.name ?? m.user?.email))}` : ''}`;
  const production = knows.state?.production ?? null;
  const version = liveVersion(production?.content);
  const liveLine = production ? (version ? `Live version ${version}, updated ${longDate(production.createdAt)}.` : `What's live was updated ${longDate(production.createdAt)}.`) : '';

  const recent = recentItems(showArchived ? all : active, meetings ?? []);
  const shown = showAll ? recent : recent.slice(0, RECENT_SHOWN);
  const decisionsCount = contextBullets(knows.context?.content, 'Decisions').length + activeMemory(knows.memory?.project).filter((i) => i.kind === 'decision').length;
  const notesCount = activeMemory(knows.memory?.project).filter((i) => i.kind !== 'decision').length + activeMemory(knows.memory?.user).length;

  return (
    <div className="page proj">
      <header className="proj-head">
        {eyebrow ? <span className="proj-eyebrow">{eyebrow}</span> : null}
        <Link to="/projects" className="link-btn small">All projects</Link>
        <h1>{project ? project.name : 'Loading...'}</h1>
        {project && (project.description || liveLine) ? (
          <p className="proj-lede">
            {project.description ? `${project.description} ` : ''}
            {liveLine}
          </p>
        ) : null}
      </header>
      {error ? <ErrorBox>{error}</ErrorBox> : null}

      {liveMeeting ? (
        <div className="proj-live" role="status">
          <span className="live-dot" aria-hidden="true" />
          <span className="proj-live-text">
            <strong>{meetingTitle(liveMeeting.title, liveMeeting.startedAt)}</strong> is on now
          </span>
          <Link to={`/meet/${liveMeeting.id}`} className="btn btn-primary" aria-label={`Join ${meetingTitle(liveMeeting.title, liveMeeting.startedAt)}`} title={`Join ${meetingTitle(liveMeeting.title, liveMeeting.startedAt)}`}>
            Join
          </Link>
        </div>
      ) : null}

      <div className={`proj-actions ${canMeet ? '' : 'proj-actions-guest'}`}>
        {!canMeet ? null : (
          <ActionCard primary icon={<MicIcon size={26} />} title="Talk with Jabberr" sub="Out loud, like a call" onClick={() => void createMeeting(true)} disabled={busy} />
        )}
        <ActionCard icon={<ChatIcon size={26} />} title="Write to Jabberr" sub="A new conversation" onClick={() => void create()} disabled={busy} />
        {!canMeet ? null : (
          <ActionCard icon={<PeopleIcon size={26} />} title="Start a meeting" sub="Invite people, Jabberr takes notes" onClick={() => void createMeeting()} disabled={busy} />
        )}
      </div>

      {project?.kind === 'personal' && !guest ? <TeamMeetingPicker /> : null}
      {canMeet && project?.kind === 'team' && project.status === 'active' ? <ProjectMeetings projectId={projectId} /> : null}

      {guest ? null : (
        <div className="proj-more">
          <button type="button" className="link-btn small proj-more-toggle" aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
            {moreOpen ? 'Fewer options' : 'More options'}
          </button>
          {moreOpen ? (
            <div className="proj-more-panel">
              <label className="proj-field">
                <span>Jabberr as</span>
                <select value={effectiveRole} onChange={(e) => setConvRole(e.target.value as ConversationRole)} aria-label="Jabberr as">
                  {roleChoices.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r)}
                    </option>
                  ))}
                </select>
                <span className="muted tiny">{ROLES[effectiveRole].description}</span>
              </label>
              <div className="proj-switches">
                <label className="switch">
                  <input type="checkbox" checked={effort === 'high'} onChange={(e) => setEffort(e.target.checked ? 'high' : 'low')} />
                  <span className="switch-track" />
                  <span>Think longer before answering</span>
                </label>
                <span className="muted tiny">Web search on</span>
              </div>
              <div className="proj-more-actions">
                <button type="button" className="btn" onClick={() => setWriting((w) => !w)} aria-expanded={writing} disabled={active.length === 0}>
                  {writing ? 'Close' : 'Write a document from conversations...'}
                </button>
                <span className="muted tiny">These apply to the next conversation you start here. Cmd/Ctrl+K starts one.</span>
              </div>
              {writing && project ? <WritePanel conversations={active} project={project} onClose={() => setWriting(false)} onError={setError} /> : null}
            </div>
          ) : null}
        </div>
      )}

      <div className="proj-columns">
        <section className="proj-recent" aria-labelledby="proj-recent-h">
          <div className="section-head">
            <h2 id="proj-recent-h">Recent</h2>
            {archived.length > 0 ? (
              <label className="small muted row">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> show archived ({archived.length})
              </label>
            ) : null}
          </div>
          {conversations === null ? (
            <Spinner label="Loading" />
          ) : recent.length === 0 ? (
            <EmptyState title={guest ? 'Nothing here yet. Write to Jabberr to ask a question or describe a change.' : canMeet ? 'Nothing here yet. Talk, write or start a meeting above; Jabberr already knows this project.' : 'Nothing here yet. Write to Jabberr above to start.'} />
          ) : (
            <ul className="recent-list">
              {shown.map((item) =>
                item.type === 'meeting' ? (
                  <MeetingRow key={`m-${item.meeting.id}`} item={item} ending={endingMeeting === item.meeting.id} onEnd={setConfirmEndMeeting} />
                ) : (
                  <ConversationRow key={`c-${item.conversation.id}`} item={item} projectId={projectId} onArchive={(c, s) => void archive(c, s)} />
                ),
              )}
            </ul>
          )}
          {recent.length > RECENT_SHOWN ? (
            <button type="button" className="link-btn small recent-more" onClick={() => setShowAll((s) => !s)}>
              {showAll ? 'Show fewer' : `Show all ${recent.length}`}
            </button>
          ) : null}
        </section>

        <aside className="proj-knows" aria-labelledby="proj-knows-h">
          <div className="section-head">
            <h2 id="proj-knows-h">What Jabberr knows</h2>
          </div>
          <ul className="knows-links">
            <KnowsLink to={`/p/${projectId}/knows`} label="What's live now" value={knows.loading ? '' : production ? shortDate(production.createdAt) : 'Not yet'} />
            {guest ? null : (
              <>
                <KnowsLink to={`/p/${projectId}/knows`} label="Decisions and plans" value={knows.loading ? '' : `${decisionsCount}`} />
                <KnowsLink to={`/p/${projectId}/knows`} label="What it remembers" value={knows.loading ? '' : `${notesCount} ${notesCount === 1 ? 'note' : 'notes'}`} />
                <KnowsLink to={`/p/${projectId}/outputs`} label="Saved documents" value={knows.outputs ? `${knows.outputs.length}` : ''} />
              </>
            )}
          </ul>
        </aside>
      </div>

      {project?.kind !== 'personal' && !guest ? (
        <section>
          <div className="section-head">
            <h2>
              Members <span className="count">{members.length}</span>
            </h2>
          </div>
          <div className="member-row">
            {(members.slice(0, MAX_MEMBERS_SHOWN)).map((m) => (
              <span key={m.userId} className="member-circle" title={m.user?.name ?? m.user?.email}>
                <span className="avatar" aria-hidden="true">
                  {initials(m.user?.name ?? m.userId)}
                </span>
              </span>
            ))}
            {members.length > MAX_MEMBERS_SHOWN ? (
              <span className="member-circle member-circle-more" title={`${members.length - MAX_MEMBERS_SHOWN} more`}>
                <span className="avatar" aria-hidden="true">
                  +{members.length - MAX_MEMBERS_SHOWN}
                </span>
              </span>
            ) : null}
            {members.find((m) => m.role === 'owner' && m.userId !== project?.createdBy) ? (
              <span className="member-owner-chip">owner</span>
            ) : null}
            {project?.createdBy === user?.id ? (
              <span className="member-owner-chip">owner</span>
            ) : null}
          </div>
          {project?.kind === 'private' && project.isIdea ? (
            <div className="member-manage">
              <p className="muted small">
                A new project idea, private to the people you invite. Invite teammates or guests to brainstorm with Jabberr; it stays private,
                and becomes a team project once its requirements are approved in Loop.
              </p>
              {isOwner ? <AddMember projectId={projectId} idea onAdded={(m) => setMembers((prev) => [...prev, m])} onError={setError} /> : null}
            </div>
          ) : project?.kind === 'private' ? (
            <p className="muted small">
              Only you can see this project. Share it when it is ready for the team; you then choose who joins.{' '}
              <button type="button" className="link-btn" onClick={() => setConfirmShare(true)}>
                Share with the team
              </button>
            </p>
          ) : isOwner ? (
            <div className="member-manage">
              <MemberList projectId={projectId} members={members} myId={user?.id} onRemoved={(id) => setMembers((prev) => prev.filter((m) => m.userId !== id))} onError={setError} />
              <AddMember projectId={projectId} onAdded={(m) => setMembers((prev) => [...prev, m])} onError={setError} />
              <button type="button" className="link-btn small" onClick={() => void addEveryone()} title="Adds every Tarawa team member who has a Jabberr account today">
                Add everyone
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {canInviteGuests && projectId ? <GuestInvitationPanel projectId={projectId} /> : null}

      {isOwner && project ? (
        <footer className="proj-foot">
          {canLinkAnchor ? (
            <WorkbenchProjectField
              value={project.workbenchProjectId ?? null}
              onSave={async (workbenchProjectId) => {
                try {
                  const { project: p } = await api.projects.linkAnchor(projectId, workbenchProjectId ?? undefined);
                  setProject(p);
                } catch (err) {
                  setError(errorMessage(err));
                }
              }}
            />
          ) : null}
          {project.kind !== 'personal' ? (
            <label className="nav-group-field small">
              <span className="muted">Show in the sidebar under</span>
              <select
                value={project.navGroup ?? ''}
                onChange={(e) => {
                  const navGroup = (e.target.value || null) as NavGroup | null;
                  api.projects
                    .update(projectId, { navGroup })
                    .then(({ project: p }) => {
                      setProject(p);
                      void refreshProjects();
                    })
                    .catch((err) => setError(errorMessage(err)));
                }}
              >
                <option value="">{project.kind === 'private' ? 'Just me (default)' : 'Products (default)'}</option>
                {NAV_GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {NAV_GROUP_LABELS[g]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button type="button" className="link-btn small" onClick={() => void toggleProjectStatus()}>
            {project.status === 'active' ? 'Archive this project' : 'Restore this project'}
          </button>
        </footer>
      ) : null}

      {confirmEndMeeting && confirmEndMeeting.projectId === projectId && <ConfirmDialog title="End this meeting?" body="This ends the call for everyone and saves its transcript." confirmLabel="End meeting" busy={endingMeeting !== null} onCancel={() => setConfirmEndMeeting(null)} onConfirm={() => { const meeting = confirmEndMeeting; setConfirmEndMeeting(null); void endMeeting(meeting); }} />}
      {confirmShare ? (
        <ConfirmDialog
          title="Share this project?"
          body="Its conversations, project notes, what's live and what Jabberr remembers become visible to every member you add. This cannot be undone."
          confirmLabel="Share with the team"
          busy={false}
          onConfirm={() => void share()}
          onCancel={() => setConfirmShare(false)}
        />
      ) : null}
    </div>
  );
}

function ActionCard({ icon, title, sub, onClick, disabled, primary }: { icon: ReactNode; title: string; sub: string; onClick: () => void; disabled?: boolean; primary?: boolean }) {
  return (
    <button type="button" className={`action-card ${primary ? 'action-card-primary' : ''}`} onClick={onClick} disabled={disabled}>
      <span className="action-card-icon">{icon}</span>
      <span className="action-card-title">{title}</span>
      <span className="action-card-sub">{sub}</span>
    </button>
  );
}

function KnowsLink({ to, label, value }: { to: string; label: string; value: string }) {
  return (
    <li>
      <Link to={to} className="knows-link">
        <span>{label}</span>
        <span className="muted">{value}</span>
      </Link>
    </li>
  );
}

function RowIcon({ children }: { children: ReactNode }) {
  return <span className="recent-icon" aria-hidden="true">{children}</span>;
}

function ConversationRow({ item, projectId, onArchive }: { item: Extract<RecentItem, { type: 'conversation' }>; projectId: string; onArchive: (c: Conversation, s: 'active' | 'archived') => void }) {
  const c = item.conversation;
  const title = conversationTitle(c.title);
  return (
    <li className={`recent-row ${c.status === 'archived' ? 'archived' : ''}`}>
      <Link to={`/p/${projectId}/c/${c.id}`} className="recent-main">
        <RowIcon>{c.kind === 'review' ? <TranscriptIcon /> : <ChatIcon />}</RowIcon>
        <span className="recent-info">
          <span className="recent-title">
            {title}
            {c.status === 'archived' ? <span className="chip">archived</span> : null}
          </span>
          <span className="recent-meta">{recentMeta(item)}</span>
        </span>
        <span className="recent-time">{shortDate(item.at)}</span>
      </Link>
      <button
        type="button"
        className="link-btn small recent-action"
        onClick={() => onArchive(c, c.status === 'active' ? 'archived' : 'active')}
        aria-label={`${c.status === 'active' ? 'Archive' : 'Restore'} ${title}`}
      >
        {c.status === 'active' ? 'Archive' : 'Restore'}
      </button>
    </li>
  );
}

function MeetingRow({ item, ending, onEnd }: { item: Extract<RecentItem, { type: 'meeting' }>; ending: boolean; onEnd: (m: Meeting) => void }) {
  const m = item.meeting;
  const live = m.status === 'live';
  const href = !live && m.recordingId ? `/r/${m.recordingId}` : `/meet/${m.id}`;
  return (
    <li className={`recent-row ${live ? 'live' : ''}`}>
      <Link to={href} className="recent-main">
        <RowIcon>{isTalk(m) ? <MicIcon /> : <PeopleIcon />}</RowIcon>
        <span className="recent-info">
          <span className="recent-title">
            {meetingTitle(m.title, m.startedAt)}
            {live ? (
              <span className="chip"><span className="live-dot" aria-hidden="true" /> Live</span>
            ) : null}
          </span>
          <span className="recent-meta">{recentMeta(item)}</span>
        </span>
        <span className="recent-time">{shortDate(item.at)}</span>
      </Link>
      {live ? (
        <span className="recent-live-actions">
          <Link to={`/meet/${m.id}`} className="btn btn-primary btn-small">Join</Link>
          {m.canEnd ? (
            <button type="button" className="btn btn-small" onClick={() => onEnd(m)} disabled={ending} aria-label={`End ${meetingTitle(m.title, m.startedAt)}`}>
              End
            </button>
          ) : null}
        </span>
      ) : null}
    </li>
  );
}



/** Links the project to its Anchor Yard record (OPEN-34). Yard decides the account and which Workbench projects are
 *  allowed; the owner may name one of them (for example ravens-website) or leave it blank when Yard binds just one. */
function WorkbenchProjectField({ value, onSave }: { value: string | null; onSave: (id: string | null) => Promise<void> }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const next = draft.trim() || null;
  return (
    <form
      className="row small"
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(next);
      }}
    >
      <label className="muted" htmlFor="workbench-project">
        Workbench project
      </label>
      <input id="workbench-project" value={draft} placeholder="for example ravens-website" onChange={(e) => setDraft(e.target.value)} />
      <button type="submit" className="link-btn small">
        Link through Anchor Yard
      </button>
    </form>
  );
}

/** Draft a document across threads: pick the kind, pick the source threads, optionally add notes. */


// Internal-audience roles only: a client_partner draft makes no sense here (OPEN-37, review round 2).
const WRITE_ROLES = ConversationRole.options.filter((r) => ROLES[r].audience === 'internal');

/** Draft a document across threads: pick the kind, pick the role, pick the source threads, optionally add notes. */

function WritePanel({
  conversations,
  project,
  onClose,
  onError,
}: {
  conversations: Conversation[];
  project: Project;
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  const projectId = project.id;
  const navigate = useNavigate();
  const [docs, setDocs] = useState<DocumentPrompt[] | null>(null);
  const [kind, setKind] = useState<DocumentKind>('requirements');
  const [role, setRole] = useState<ConversationRole>(defaultRoleFor(project.kind));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.outputs
      .documents()
      .then((r) => setDocs(r.documents))
      .catch((err) => onError(errorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    try {
      const { conversationId, firstTurn } = await api.projects.write(projectId, {
        purpose: kind,
        role,
        conversationIds: [...selected],
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onClose();
      navigate(`/p/${projectId}/c/${conversationId}`, { state: { firstTurn } });
    } catch (err) {
      onError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="write-panel">
      <label className="row small">
        Document
        <select value={kind} onChange={(e) => setKind(e.target.value as DocumentKind)} aria-label="Document kind">
          {(docs ?? []).map((d) => (
            <option key={d.kind} value={d.kind}>
              {d.label}
            </option>
          ))}
        </select>
        <span className="muted">{DOCUMENT_KINDS[kind].description}</span>
      </label>
      <label className="row small">
        Jabberr as
        <select value={role} onChange={(e) => setRole(e.target.value as ConversationRole)} aria-label="Draft role">
          {WRITE_ROLES.map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </select>
        <span className="muted">{ROLES[role].description}</span>
      </label>
      <fieldset className="write-threads">
        <legend className="small muted">Conversations to draw on ({selected.size} chosen)</legend>
        {conversations.map((c) => (
          <label key={c.id} className="row small write-thread">
            <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
            <span className="write-thread-title">{conversationTitle(c.title)}</span>
            <span className="muted tiny">{c.lastTurnSeq} messages</span>
          </label>
        ))}
      </fieldset>
      <label className="row small">
        Notes for the draft (optional)
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={4000} placeholder="Anything the conversations do not already say." />
      </label>
      <div className="row">
        <button type="button" className="btn btn-primary" disabled={selected.size === 0 || busy} onClick={() => void submit()}>
          {busy ? 'Opening draft...' : 'Draft it'}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function AddMember({
  projectId,
  idea = false,
  onAdded,
  onError,
}: {
  projectId: string;
  /** An idea project: teammates join as members, anyone else as a guest; no second owner. */
  idea?: boolean;
  onAdded: (m: ProjectMember) => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('member');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      const { member } = await api.projects.addMember(projectId, { email: email.trim(), role });
      onAdded(member);
      setEmail('');
    } catch (err) {
      onError(err instanceof ApiError && err.status === 404 ? `${email} has no Jabberr account yet (sign-up is invite-only).` : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="row wrap" onSubmit={(e) => void submit(e)}>
      <input type="email" placeholder="colleague@tarawa.group" value={email} onChange={(e) => setEmail(e.target.value)} required aria-label="Member email" />
      <select value={role} onChange={(e) => setRole(e.target.value as MemberRole)} aria-label="Role">
        <option value="member">{idea ? 'teammate' : 'member'}</option>
        {idea ? <option value="guest">guest</option> : <option value="owner">owner</option>}
      </select>
      <button type="submit" className="btn" disabled={busy || !email}>
        {idea ? 'Invite' : 'Add member'}
      </button>
    </form>
  );
}

/** Who is in the project, for its owners, with Remove (audit 2026-10-09 S3). */
function MemberList({
  projectId,
  members,
  myId,
  onRemoved,
  onError,
}: {
  projectId: string;
  members: ProjectMember[];
  myId: string | undefined;
  onRemoved: (userId: string) => void;
  onError: (msg: string) => void;
}) {
  const [removing, setRemoving] = useState<ProjectMember | null>(null);
  const [busy, setBusy] = useState(false);
  const label = (m: ProjectMember): string => m.user?.name || m.user?.email || 'Team member';
  const remove = async (): Promise<void> => {
    if (!removing) return;
    setBusy(true);
    try {
      await api.projects.removeMember(projectId, removing.userId);
      onRemoved(removing.userId);
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
      setRemoving(null);
    }
  };
  return (
    <>
      <ul className="member-list" aria-label="People in this project">
        {members.map((m) => (
          <li key={m.userId} className="member-list-row">
            <span className="member-list-name">{label(m)}</span>
            {m.user?.email ? <span className="muted small">{m.user.email}</span> : null}
            <span className="muted small">{m.role === 'guest' ? 'guest' : m.role}</span>
            {m.userId !== myId ? (
              <button type="button" className="link-btn small" onClick={() => setRemoving(m)}>
                Remove
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {removing ? (
        <ConfirmDialog
          title={`Remove ${label(removing)}?`}
          body="They stop seeing this project and its conversations straight away. Their other projects are not changed. You can add them again later."
          confirmLabel="Remove"
          busy={busy}
          onConfirm={() => void remove()}
          onCancel={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}
